import React, { createContext, useContext, ReactNode } from "react";
import { AppTheme } from "./theme.types";
import defaultTheme from "./defaultTheme";

// Create theme context
const ThemeContext = createContext<AppTheme>(defaultTheme);

interface ThemeProviderProps {
  theme?: Partial<AppTheme>;
  children: ReactNode;
}

/**
 * ThemeProvider component
 *
 * Provides theme context to the application, allowing for customization
 * of UI appearance throughout the app. It merges any custom theme values
 * with the default theme.
 *
 * @param {Partial<AppTheme>} theme - Optional custom theme overrides
 * @param {ReactNode} children - Child components that will have access to the theme
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  theme,
  children,
}) => {
  // Deep merge the custom theme with the default theme
  const mergedTheme = theme ? deepMerge(defaultTheme, theme) : defaultTheme;

  return (
    <ThemeContext.Provider value={mergedTheme}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * useTheme hook
 *
 * Provides access to the current theme within components
 *
 * @returns {AppTheme} The current theme
 */
export const useTheme = (): AppTheme => {
  const theme = useContext(ThemeContext);
  if (!theme) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return theme;
};

/**
 * Helper function to deep merge objects
 * Source: overrides properties in target
 */
function deepMerge<T extends Record<string, any>>(
  target: T,
  source: Partial<T>
): T {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      const typedKey = key as keyof typeof source;
      if (isObject(source[typedKey])) {
        if (!(typedKey in target)) {
          Object.assign(output, { [typedKey]: source[typedKey] });
        } else {
          const targetValue = target[typedKey];
          const sourceValue = source[typedKey];

          // Ensure both values are objects before merging
          if (isObject(targetValue) && isObject(sourceValue)) {
            // Use type assertion to tell TypeScript this is safe
            output[typedKey] = deepMerge(
              targetValue as Record<string, any>,
              sourceValue as Record<string, any>
            ) as T[keyof T];
          } else {
            // If not both objects, just use the source value
            output[typedKey] = sourceValue as T[keyof T];
          }
        }
      } else {
        Object.assign(output, { [typedKey]: source[typedKey] });
      }
    });
  }

  return output;
}

/**
 * Helper function to check if a value is an object
 */
function isObject(item: any): item is Record<string, any> {
  return item && typeof item === "object" && !Array.isArray(item);
}
